import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { PackageModel, QuoteModel, VoyageModel } from '@data-layer/models/lcl';
import { ReplaySubject } from 'rxjs';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FormHelper } from '@helpers/form.helper';
import { ProductDetailsModel } from '@data-layer/models/common';
import { LclContainerService } from '../../services';
import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { debounceTime, filter, skip, take, takeUntil } from 'rxjs/operators';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { HeaderType } from '@app/modules/app-layout/enums/headerType';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';
import { enumShippingRole } from '@data-layer/enums/user';
import { LclEditPackageComponent, LclEntityEditComponent } from '../../components';
import { AppService } from '@app/app.service';
import { TranslateService } from '@ngx-translate/core';
import { CompanyModel } from '@data-layer/models/companies';
import { enumCompanyPlacement } from '@enum/enumCompanyPlacement';
import { EditCompanyComponent } from '@app/modules/company/components';
import { CompaniesService } from '@services/companies.service';
import { ENTITY_INFO_DESC } from '../../constants';
import { enumDetailedInfoPlacementEnum } from '@app/data-layer/enums/lcl';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { INDIA_COUNTRY_CODE } from '@constants/constants';
import { GenericService } from '@services/generic.service';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { enumCbpAssignedNumberStatus } from '@app/data-layer/enums/common';

@Component({
  selector: 'app-lcl-edit-booking',
  templateUrl: './lcl-edit-booking.component.html',
  styleUrls: ['./lcl-edit-booking.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclEditBookingComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public company: CompanyModel;
  @Input() public booking: QuoteModel;
  @Input() public voyages: VoyageModel[];
  @Input() public pendingBills: PaymentBillItemPendingModel;
  @Input() public pendingMarineInsuranceBills: PaymentBillItemPendingModel;
  @Input() public productsByCompany: ProductDetailsModel[];
  @Input() public isBillLoading: boolean;
  @Input() public isManagementView: boolean;
  @Input() public isInsuranceAvailable: boolean;
  @Input() public isInsuranceLoading: boolean;
  @Input() public currentInsurance: MarineInsuranceModel;
  @Input() public marineInsuranceRequested: boolean;
  @Input() public isVoyageValidForInsurance: boolean;
  @Output() public loadBills: EventEmitter<void> = new EventEmitter<void>();
  @Output() public loadQuote: EventEmitter<void> = new EventEmitter<void>();
  @Output() public updateQuote: EventEmitter<QuoteModel> = new EventEmitter<QuoteModel>();
  @Output() public updateNoHazardousGoodsClaimed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public updateVoyage: EventEmitter<number> = new EventEmitter<number>();
  @Output() public bookQuote: EventEmitter<{ quote: QuoteModel; shipper: EntityModel }> = new EventEmitter<{
    quote: QuoteModel;
    shipper: EntityModel;
  }>();
  @Output() public updateEntity: EventEmitter<EntityModel> = new EventEmitter<EntityModel>();
  @Output() public updateQuoteAndEntity: EventEmitter<{ quote: QuoteModel; entity: EntityModel }> = new EventEmitter<{
    quote: QuoteModel;
    entity: EntityModel;
  }>();
  @Output() public createEntityAndUpdateQuote: EventEmitter<void> = new EventEmitter<void>();
  @Output() public updateIncoterms: EventEmitter<{ exportLicenseRequested: boolean; incoTermsType: enumIncotermsType }> = new EventEmitter<{
    exportLicenseRequested: boolean;
    incoTermsType: enumIncotermsType;
  }>();
  @Output() public updateQuoteDraft: EventEmitter<QuoteModel> = new EventEmitter<QuoteModel>();
  @Output() public optionLCLUpdated: EventEmitter<QuoteModel> = new EventEmitter<QuoteModel>();
  @Output() public optionMarineInsuranceUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();

  public formGroup: UntypedFormGroup;
  public packagesFormArray: UntypedFormArray;
  public voyageIdControl: UntypedFormControl;
  public isShowError: boolean = false;
  public isSubmitted: boolean;
  public recalcFormLoading: boolean;

  public ENTITY_INFO_DESC: typeof ENTITY_INFO_DESC = ENTITY_INFO_DESC;
  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;
  public buyerEntity: EntityModel;
  public sellerEntity: EntityModel;
  public notifyPartyEntity: EntityModel;
  public newIncotermsValue: enumIncotermsType;
  public newShippingRoleValue: enumShippingRole;
  public enumLclTrackingStates: enumShippingRole;
  public initialVoyageId: number;

  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public enumDetailedInfoPlacementEnum: typeof enumDetailedInfoPlacementEnum = enumDetailedInfoPlacementEnum;

  public isDesktop: boolean;

  public isShowMonsoonAlert: boolean;
  public isMonsoonAlertShown: boolean;

  private isNotFirstChange: boolean;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private lclContainerService: LclContainerService,
    private appLayoutService: AppLayoutService,
    private companiesService: CompaniesService,
    private appService: AppService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private genericService: GenericService,
  ) {}

  public ngOnInit(): void {
    this.formGroup = this.fh.createQuoteFormGroup();
    this.voyageIdControl = this.formGroup.get('voyageId') as UntypedFormControl;
    this.packagesFormArray = this.formGroup.get('packages') as UntypedFormArray;

    this.setFormData();
    this.setEntityData();

    this.initialVoyageId = this.booking?.voyage?.id;

    this.formGroup
      .get('voyageId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((voyageId: number) => this.updateVoyage.emit(voyageId));

    this.formGroup
      .get('noHazardousGoodsClaimed')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => {
        this.updateNoHazardousGoodsClaimed.emit(value);
      });

    this.formGroup
      .get('incoTermsType')
      .valueChanges.pipe(skip(1), debounceTime(50), takeUntil(this.destroy$))
      .subscribe((incoTermsType: enumIncotermsType) => {
        let exportLicenseRequested: boolean = this.formGroup.get('exportLicenseRequested').value;

        // #10181
        if (incoTermsType === enumIncotermsType.FOB) {
          exportLicenseRequested = false;
        }

        this.updateQuote.emit({
          ...this.booking,
          shippingRole: this.formGroup.get('shippingRole').value,
          incoTermsType,
          exportLicenseRequested,
        });
      });

    if (!this.isManagementView) {
      this.appLayoutService.headerTypeSource$.next(HeaderType.LCLBooking);

      //#11683
      const company = this.companiesService.getCompany();
      if (this.isAssignedCbpRequestSubmitted(company)) {
        return;
      }

      if (!this.companiesService.validateCompany(enumCompanyPlacement.SHIPMENT)) {
        this.fillCompany();
        return;
      }
    } else {
      this.formGroup.disable();
    }

    this.genericService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop: boolean) => {
      this.isDesktop = isDesktop;
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.booking) {
      this.setEntityData();
      this.setFormData();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.appLayoutService.headerTypeSource$.next(0);
  }

  public fillCompany(): void {
    const formGroup: UntypedFormGroup = this.companiesService.createCompanyForm(enumCompanyPlacement.SHIPMENT);
    const componentSidebarRef: ComponentRef<EditCompanyComponent> = this.appService.openSidebar(EditCompanyComponent, {
      sidebarHeaderText: 'LABELS.COMPLETE_REGISTRATION',
      tooltipText: 'LABELS.COMPLETE_REGISTRATION_HINT',
    });
    componentSidebarRef.instance.formGroup = formGroup;
    componentSidebarRef.instance.updateCompanySource$.pipe(takeUntil(this.destroy$)).subscribe((companyForm: UntypedFormGroup) => {
      !companyForm.valid ? formGroup.markAllAsTouched() : this.setEmptyEntitiesToQuote();
    });
  }

  public setEntityData(): void {
    this.sellerEntity = this.booking.shipper;
    this.buyerEntity = this.booking.consignee;
    this.notifyPartyEntity = this.booking.notifyParty;

    this.newShippingRoleValue = this.booking.shippingRole;
    this.newIncotermsValue = this.booking.incoTermsType;
  }

  public setFormData(): void {
    if (!!this.booking && !!this.formGroup) {
      this.packagesFormArray.clear();

      for (let i: number = 0; i < (this.booking?.packages || []).length; i++) {
        this.packagesFormArray.push(this.fh.createPackageFormGroup());
      }

      this.formGroup.patchValue(
        {
          ...this.booking,
          voyageId: this.booking?.voyage?.id,
          packages: this.lclContainerService.convertPackagesDataForForm(this.booking.packages),
        },
        { emitEvent: false },
      );

      if (!this.isManagementView) {
        this.packagesFormArray.controls.forEach((item: AbstractControl) => item.get('isView').setValue(true));
      }

      this.changeErrorState(!(this.isFormValid && this.isEntityValid(this.buyerEntity) && this.isEntityValid(this.sellerEntity)));
    }

    this.recalcFormLoading = false;
  }

  public onSubmit(): void {
    this.isSubmitted = true;

    if (!this.companiesService.validateCompany(enumCompanyPlacement.SHIPMENT)) {
      this.fillCompany();
      return;
    }

    this.changeErrorState(false);

    this.fh.validateAllFields(this.formGroup);
    if (this.isFormValid && this.isEntityValid(this.buyerEntity) && this.isEntityValid(this.sellerEntity)) {
      // #9444
      if (this.booking?.voyage?.route?.departurePort?.country?.code === INDIA_COUNTRY_CODE && !this.isMonsoonAlertShown) {
        this.isShowMonsoonAlert = true;
        this.isMonsoonAlertShown = true;
        return;
      }

      this.bookQuote.emit({
        quote: this.booking,
        shipper: this.booking?.shipper,
      });
    } else {
      this.changeErrorState(true);
      this.cdr.detectChanges();
    }
  }

  public onOptionISFUpdated(isChecked: boolean): void {
    this.formGroup.get('isfRequested').setValue(isChecked);

    this.optionLCLUpdated.emit({ ...this.booking, isfRequested: this.formGroup.get('isfRequested').value });
  }

  public onOptionESUpdated(isChecked: boolean): void {
    this.formGroup.get('entrySummaryRequested').setValue(isChecked);

    this.optionLCLUpdated.emit({ ...this.booking, entrySummaryRequested: this.formGroup.get('entrySummaryRequested').value });
  }

  public onOptionExportLicenseUpdated(isChecked: boolean): void {
    this.formGroup.get('exportLicenseRequested').setValue(isChecked);

    this.optionLCLUpdated.emit({ ...this.booking, exportLicenseRequested: this.formGroup.get('exportLicenseRequested').value });
  }

  public onOptionMarineInsuranceUpdated(isChecked: boolean): void {
    this.formGroup.get('marineInsuranceRequested').setValue(isChecked);

    this.optionMarineInsuranceUpdated.emit(isChecked);
  }

  public confirmPackage(): void {
    this.recalcFormLoading = true;
    this.packagesFormArray.controls.forEach((item: AbstractControl) => item.get('isView').setValue(true));
    this.onSavePackages(
      (this.packagesFormArray.value || []).map((item: PackageModel) => ({
        ...item,
        id: !!item?.id ? item.id : undefined,
      })),
    );
  }

  public addMobilePackage(): void {
    if (this.packagesFormArray.valid) {
      const firstPackage: PackageModel = this.packagesFormArray.getRawValue()[0];
      this.packagesFormArray.push(this.fh.createPackageFormGroup(firstPackage?.lengthUnit, firstPackage?.weightUnit));
    } else {
      this.fh.markAsTouched(this.formGroup);
      this.cdr.markForCheck();
    }
  }

  public addPackage(): void {
    const componentSidebarRef: ComponentRef<LclEditPackageComponent> = this.appService.openSidebar(LclEditPackageComponent, {
      sidebarHeaderText: 'EDIT_LOAD_DETAILS',
    });

    componentSidebarRef.instance.packages = [this.fh.createPackageFormGroup().getRawValue()];
    componentSidebarRef.instance.isAdminView = this.isManagementView;
    componentSidebarRef.instance.isUpdatedLayout = true;
    componentSidebarRef.instance.isShowClear = true;
    componentSidebarRef.instance.dataSaved.pipe(take(1)).subscribe((val: PackageModel[]) => {
      this.recalcFormLoading = true;

      const result: PackageModel[] = this.lclContainerService.convertSidebarEditSingleData(
        [...this.booking.packages, ...val],
        this.booking.packages?.[0],
        val,
        this.booking.packages?.length + 1,
      );

      this.onSavePackages(result);
      this.appService.closeSidebar();
    });
  }

  public editPackage(id: number): void {
    const componentSidebarRef: ComponentRef<LclEditPackageComponent> = this.appService.openSidebar(LclEditPackageComponent, {
      sidebarHeaderText: 'EDIT_LOAD_DETAILS',
    });

    const formItem: AbstractControl = this.fh.createPackageFormGroup();
    const packageData: PackageModel = this.booking.packages.find((quotePackage: PackageModel, index: number) => index === id);
    formItem.patchValue(packageData);

    componentSidebarRef.instance.packages = [formItem.value];
    componentSidebarRef.instance.isAdminView = this.isManagementView;
    componentSidebarRef.instance.isUpdatedLayout = true;
    componentSidebarRef.instance.isShowClear = true;
    componentSidebarRef.instance.dataSaved.pipe(take(1)).subscribe((val: PackageModel[]) => {
      this.recalcFormLoading = true;

      const result: PackageModel[] = this.lclContainerService.convertSidebarEditSingleData(this.booking.packages, packageData, val, id);

      this.onSavePackages(result);
      this.appService.closeSidebar();
    });
  }

  public onRemovePackage(id: number): void {
    this.recalcFormLoading = true;

    this.updateQuote.emit({
      ...this.booking,
      packages: this.booking.packages.filter((quotePackage: PackageModel, index: number) => index !== id),
    });
  }

  public onSavePackages(packages: PackageModel[]): void {
    packages = this.lclContainerService.convertPackagesDataForUpdateSending(packages || []);

    this.updateQuote.emit({ ...this.booking, packages });
  }

  public get isFormValid(): boolean {
    return this.formGroup.valid;
  }

  public onEditEntity(entity: EntityModel, entityCode: enumEntityModelCode, isCreate?: boolean, isUpdateRole?: boolean): void {
    if (this.isManagementView) {
      return;
    }

    let headerLabel: string = 'INFO.SH.TITLE_LCL';
    switch (entityCode) {
      case enumEntityModelCode.CONSIGNEE:
        headerLabel = 'INFO.BY.TITLE_LCL';
        break;
      case enumEntityModelCode.NOTIFY_PARTY:
        headerLabel = 'INFO.NP.TITLE_LCL';
        break;
    }

    const componentSidebarRef: ComponentRef<LclEntityEditComponent> = this.appService.openSidebar(LclEntityEditComponent, {
      sidebarHeaderText: `${this.translateService.instant(
        isCreate || !entity ? 'BUTTON.ADD' : 'BUTTON.EDIT',
      )} ${this.translateService.instant(headerLabel)}`,
    });

    componentSidebarRef.instance.entity = { ...entity, entityCode };
    componentSidebarRef.instance.entityCode = entityCode;
    componentSidebarRef.instance.incotermsType = this.booking.incoTermsType;
    componentSidebarRef.instance.shippingRole = this.booking.shippingRole;
    componentSidebarRef.instance.isLCLForeign = true;

    if (isUpdateRole) {
      componentSidebarRef.instance.isUpdateIncoterms = isUpdateRole;
      componentSidebarRef.instance.incotermsType = this.newIncotermsValue;
      componentSidebarRef.instance.shippingRole = this.newShippingRoleValue;
    }

    componentSidebarRef.instance.dataSaved.pipe(takeUntil(this.destroy$)).subscribe((entityData: EntityModel) => {
      this.prepareDataQuoteAndEntity(
        {
          ...this.booking,
          shippingRole: this.newShippingRoleValue,
          incoTermsType: this.newIncotermsValue,
        },
        {
          ...entityData,
          entityCode,
        },
      );

      this.appService.closeSidebar();
    });
    componentSidebarRef.instance.incotermsUpdated.pipe(takeUntil(this.destroy$)).subscribe((val: enumIncotermsType) => {
      this.newIncotermsValue = val;
    });

    if (entityCode !== enumEntityModelCode.NOTIFY_PARTY) {
      this.appService.isClickOnCloseSubject
        .pipe(
          filter((val: boolean) => !!val),
          take(1),
        )
        .subscribe(() => {
          this.prepareDataQuoteAndEntity(this.booking, {
            ...entity,
            entityCode,
          });
        });
    }
  }

  public entitySameAsCompany(entityToCompare: EntityModel): string {
    if (!this.company || !entityToCompare) {
      return '';
    }

    if (
      this.company?.businessName === entityToCompare.name &&
      this.company?.address?.line1 === entityToCompare?.address?.line1 &&
      this.company?.address?.line2 === entityToCompare?.address?.line2 &&
      this.company?.address?.line3 === entityToCompare?.address?.line3 &&
      this.company?.address?.city === entityToCompare?.address?.city &&
      this.company?.address?.state?.id === entityToCompare?.address?.state?.id &&
      this.company?.address?.postalCode === entityToCompare?.address?.postalCode
    ) {
      return this.translateService.instant('LABELS.AUTOMATICALLY_FROM_PROFILE');
    }
  }

  public updateShippingRole(entity: EntityModel, entityCode: enumEntityModelCode): void {
    if (this.isManagementView) {
      return;
    }

    let newRole: enumShippingRole;
    let newEntityCode: enumEntityModelCode;
    let newIncotermsValue: enumIncotermsType = this.booking.incoTermsType;
    let entityToEdit: EntityModel = { ...entity, entityCode };

    newRole = this.booking.shippingRole === enumShippingRole.IMPORTER ? enumShippingRole.SHIPPER : enumShippingRole.IMPORTER;
    newEntityCode = this.booking.shippingRole === enumShippingRole.IMPORTER ? enumEntityModelCode.SHIPPER : enumEntityModelCode.CONSIGNEE;

    switch (this.booking.incoTermsType) {
      case enumIncotermsType.FCA:
        newIncotermsValue = enumIncotermsType.DAT;
        break;
      case enumIncotermsType.FOB:
        newIncotermsValue = enumIncotermsType.CPT_CFR;
        break;
      case enumIncotermsType.DAT:
        newIncotermsValue = enumIncotermsType.FCA;
        break;
      case enumIncotermsType.CPT_CFR:
        newIncotermsValue = enumIncotermsType.FOB;
        break;
    }

    this.newShippingRoleValue = newRole;
    this.newIncotermsValue = newIncotermsValue;

    const newQuote: QuoteModel = {
      ...this.booking,
      shippingRole: newRole,
      incoTermsType: newIncotermsValue,
    };
    if (newEntityCode === enumEntityModelCode.CONSIGNEE) {
      entityToEdit = this.booking.consignee;
      newQuote.consignee = newQuote.shipper;
      newQuote.shipper = entityToEdit;
    }
    if (newEntityCode === enumEntityModelCode.SHIPPER) {
      entityToEdit = this.booking.shipper;
      newQuote.shipper = newQuote.consignee;
      newQuote.consignee = entityToEdit;
    }

    this.booking = { ...this.booking, ...newQuote };
    this.sellerEntity = newQuote.shipper;
    this.buyerEntity = newQuote.consignee;

    this.updateQuoteDraft.emit({ ...this.booking, incoTermsType: this.newIncotermsValue });
    this.onEditEntity(entityToEdit, entityCode, false, true);
  }

  public isShowChangeRole(entityCode: enumEntityModelCode): boolean {
    switch (this.booking?.shippingRole) {
      case enumShippingRole.IMPORTER:
        return entityCode === enumEntityModelCode.CONSIGNEE;
      case enumShippingRole.SHIPPER:
        return entityCode === enumEntityModelCode.SHIPPER;
      default:
        return false;
    }
  }

  public setIncoterms(incoTermsType: number): void {
    this.formGroup.get('incoTermsType').setValue(incoTermsType);
  }

  public setShippingRole(state: enumShippingRole): void {
    this.formGroup.get('shippingRole').setValue(state);
    let newIncotermsValue: enumIncotermsType = this.booking.incoTermsType;

    switch (this.formGroup.get('incoTermsType').value) {
      case enumIncotermsType.FCA:
        newIncotermsValue = enumIncotermsType.DAT;
        break;
      case enumIncotermsType.FOB:
        newIncotermsValue = enumIncotermsType.CPT_CFR;
        break;
      case enumIncotermsType.DAT:
        newIncotermsValue = enumIncotermsType.FCA;
        break;
      case enumIncotermsType.CPT_CFR:
        newIncotermsValue = enumIncotermsType.FOB;
        break;
    }

    this.setIncoterms(newIncotermsValue);
  }

  public isEntityValid(entity: EntityModel): boolean {
    return !!entity && !!entity.name && !!entity.email && !!entity.phone;
  }

  public onMonsoonAlertChange(val: boolean): void {
    if (this.isShowMonsoonAlert !== val) {
      this.isShowMonsoonAlert = val;
      if (!val && this.isMonsoonAlertShown) {
        this.onSubmit();
      }
    }
  }

  public get incotermsHeaderLabel(): string {
    switch (this.booking.shippingRole) {
      case enumShippingRole.IMPORTER:
        return 'LABELS.INCOTERMS_SWITCH_BUYER';
      case enumShippingRole.SHIPPER:
        return 'LABELS.INCOTERMS_SWITCH_SELLER';
      default:
        return '';
    }
  }

  private isAssignedCbpRequestSubmitted(company: CompanyModel): boolean {
    return (
      company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENT ||
      company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENDING
    );
  }

  private changeErrorState(state: boolean): void {
    this.isShowError = state;
  }

  private setEmptyEntitiesToQuote(): void {
    this.createEntityAndUpdateQuote.emit();

    this.appService.closeSidebar();
  }

  private prepareDataQuoteAndEntity(quote: QuoteModel, entity: EntityModel): void {
    this.updateQuoteAndEntity.emit({
      quote,
      entity,
    });
  }
}
